import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { QueryKeys } from "queryClient";
import { InfiniteEffortRuleList } from "types";
import {
  getAllEffortRuleList,
  getEffortRuleList,
} from "modules/effortrules/api";
import { useEffortRuleListFilterAndSort } from "../useEffortRuleListFilterAndSort";

export const useEffortRuleList = (filter = true) => {
  const filterAndSortEffortRules = useEffortRuleListFilterAndSort(filter);

  const [loadAll, setLoadAll] = useState(false);

  const { data, fetchNextPage, hasNextPage, isLoading } =
    useInfiniteQuery<InfiniteEffortRuleList>({
      queryKey: loadAll
        ? [QueryKeys.EffortRules, "all"]
        : [QueryKeys.EffortRules],
      queryFn: ({ pageParam }) =>
        loadAll
          ? getAllEffortRuleList()
          : getEffortRuleList(pageParam as string | null),
      getNextPageParam: (lastPage) => lastPage.nextToken ?? undefined,
      initialPageParam: null,
      staleTime: 1000 * 60 * 15, // 15 minutes
      gcTime: 1000 * 60 * 60, // 1 hour
    });

  const effortRuleList = useMemo(() => {
    const effortRules = data?.pages
      ? data.pages.flatMap((page) => page.effortRuleList)
      : [];

    return filterAndSortEffortRules(effortRules);
  }, [data, filterAndSortEffortRules]);

  return {
    effortRuleList,
    fetchNextPage: (all?: boolean) => {
      if (all) setLoadAll(true);
      fetchNextPage();
    },
    hasNextPage,
    isLoading,
  };
};
